import { ClientContacts } from '@/modules/client-contacts/types';
import { Products } from '@/modules/products/types';

export namespace DataPilot {
  export enum Pages {
    DATA_PILOT = 'data-pilot',
    SMART_RADAR = 'smartradar'
  }

  export enum EventStatuses {
    NEW = 'New',
    VIEW = 'View',
    DONE = 'Done',
    CANCEL = 'Cancel',
    IN_WORK= 'In_work',
  }

  export interface Tag {
    id: number,
    name: string,
  }

  export interface Model {
    id: number,
    name: string,
  }

  export interface Event {
    id: number,
    name: string,
    color: string,
  }

  export interface ResponsibleClient {
    id: number,
    name: string,
  }

  export interface EventStatus {
    id: number,
    name: string,
    slug: string,
  }

  export interface ResponsibleEvent {
    id: number,
    name: string,
  }

  export interface Priority {
    id: number,
    color: string,
    name: string,
    sort: number,
  }

  export interface ClientEventTag {
    id: number;
    name: string;
    pivot: {
      client_event_type_id: number;
      client_event_tag_id: number;
    };
  }

  export interface ClientEventType {
    id: number;
    client_event_tags: ClientEventTag[];
    client_event_priority: Priority
  }

  export interface Mark {
    id: number;
    name: string;
  }

  export interface Okved {
    id: number;
    name: string;
  }

  export interface ClientCategory {
    id: number;
    name: string;
  }

  export interface TaxSystem {
    id: number;
    name: string;
  }

  export interface Step {
    id: number;
    name: string;
  }

  export interface Action {
    value: string;
    name: string;
    description: string;
    icon: string;
  }

  export interface MassUpdateFilters {
    responsible_client_ids?: number[] | null;
    mark_ids?: number[] | null;
    cities?: string[];
    regions?: string[];
    okved_ids?: number[];
    client_category_ids?: number[] | null;
    tax_system_ids?: number[];
    client_status_ids?: number[] | null;
    product_category_ids?: number[] | null;
    tag_ids?: number[] | null;
    model_ids?: number[];
    client_event_ids?: number[];

    event_status_ids?: number[] | null;
    responsible_event_ids?: number[] | null;
    event_ids?: number[] | null;
    priority_ids?: number[] | null;

    period?: string[] | null;
    is_sign?: boolean | null,
    is_excise?: boolean | null,
  }

  export interface MassUpdateRequest {
    responsible_id?: number | null;
    status_id?: number | null;
    comment?: string;
    mark_ids?: number[];
    mark_action?: string | null;
    new_marks?: string[];
    is_notification?: boolean,
    filters: MassUpdateFilters;
  }

  export interface ActionParams {
    responsible_id: number | null;
    status_id: number | null;
    comment: string;
    mark_ids: number[];
    mark_action: string | null;
    new_marks: string[];
    is_notification: boolean,
  }

  export interface ClientEvent {
    id: number;
    client_event_type_id: number;
    type_name: string;
    status_slug: string;
    comment: string | null;
    date: string;
    address: string;
    serial: string;
    model_name: string;
    user_name: string;
    user_id: number;
    status_name: string;
    client_event_status_id: number;
    priority_color: string;
    url: string;
    client_event_type: ClientEventType;
    device_id: number;
    client_id: number;
    client_event_id: number;
    client_events_count: number;
    product: Products.CatalogProductItem;
  }

  export interface EventPriorityCount {
    id: number;
    name: string;
    color: string;
    sort: number,
    count: number;
  }

  export interface DeviceCoord {
    id: number;
    client_id: number;
    address: string | null;
    coord: [number, number];
    deviceCount: number;
    eventCount: number;
    eventData: EventPriorityCount[];
    eventDataSignature: string | null;
  }

  export interface ClientStatus {
    id: number,
    name: string,
  }

  export interface ProductCategory {
    id: number,
    name: string,
  }

  export interface Client {
    id: number;
    name: string;
    name_organization: string | null;
    is_active: boolean;
    address: string | null;
    region: string | null;
    city: string | null;
    kpp: string | null;
    created_at: string | null;
    is_mark: string | null;
    is_resident: boolean,

    client_marks: Mark[];

    client_excise_product_categories: {
      id: number;
      name: string | null;
    }[];

    client_product_categories: {
      id: number;
      name: string | null;
    }[];

    client_tax_systems: {
      id: number;
      name: string | null;
    }[];

    client_category: {
      id: number;
      name: string | null;
    } | null;

    okved: {
      id: number;
      name: string | null;
    } | null;

    contact: {
      email: string | null;
      telephone: string | null;
    };

    organization: {
      id: number;
      name: string | null;
    } | null;

    user: {
      id: number;
      name: string;
    };

    client_status: {
      id: number;
      name: string;
    };

    client_events: {
      client_event_type: {
        id: number;
        name: string | null;
        client_event_priority: {
          id: number;
          color: string;
          name: string;
          sort: number,
        };
      };
    }[];

    client_contacts?: ClientContacts.ClientContact[],
  }

  export interface Device {
    id: number;
    client_id: number;
    address: string | null;

    point_longitude: string | number;
    point_latitude: string | number;

    client_events: ClientEvent[];

    client: Client,

    url: string | null,
  }

  export interface PointEvent {
    id: number;
    name: string;
    color: string;
    count: number;
    sort: number;
  }
}

export interface Filters {
  search: string | null,
  tag_ids: number[] | null,
  event_status_ids: number[] | null,
  priority_ids: number[] | null,
  period: string[] | null,
  event_ids: number[] | null,
  client_status_ids: number[] | null,
  responsible_client_ids: number[] | null,
  responsible_event_ids: number[] | null,
  category_ids: number[] | null,
  client_id: number | null,
  mark_ids: number[],
}

export interface PriorityGroup {
  id: number;
  name: string;
  event_name: string | null;
  color: string;
  sort: number,
  count: number;
}

export interface ResponsibleClient {
  id: number | null
  name: string
}

export enum ClientActions {
  ASSIGN_RESPONSIBLE = 'assign-responsible',
  COMMENT = 'comment',
  MARKS = 'marks',
}

export enum EventActions {
  EVENT_STATUS = 'event-status',
  EVENT_RESPONSIBLE = 'event-responsible',
}

export enum SelectedObject {
  CLIENTS = 'clients',
  EVENTS = 'events',
}

export enum SelectionsMode {
  MANUAL = 'manual',
  ACTIVE = 'active',
}

export enum MarkActions {
  ADD = 'add',
  CLEAR = 'clear',
  REPLACE = 'replace',
  DELETE = 'delete',
}

export enum SelectedActions {
  ASSIGN_RESPONSIBLE = 'assign-responsible',
  EVENT_STATUS = 'event-status',
  EVENT_RESPONSIBLE = 'event-responsible',
  COMMENT = 'comment',
  MARKS = 'marks',
}
